import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useNavigate } from '@change-corgi/core-react/router';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionAngleData } from '#src/app/shared/api/socialStarter';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';
import { appendSourceLocationToSapParams, appendVideoConsentToSapParams } from '#src/app/shared/utils/sap';

import type { UseClaimPetitionPageData } from './types';
import { useSaveClaimedDraft } from './useSaveClaimedDraft';

type UseClaimPetitionOptions = {
	readonly draft: PetitionAngleData;
	/**
	 * Opts into speculative image generation on mount so page dwell can hide generation latency.
	 * This can generate an image for a visitor who never claims; the accepted tradeoff is scoped to
	 * this front-end MVP. Preferences consent enables persisted reuse in the same browser and Social
	 * Starter token; otherwise reuse lasts only for the current page. When omitted, image generation
	 * starts on claim using `draft`.
	 */
	readonly prewarmImageDraft?: PetitionAngleData;
	readonly pageData: UseClaimPetitionPageData;
	readonly returnPath?: string;
	readonly fallbackErrorMessage?: string;
	readonly keepLoadingAfterSuccess?: boolean;
	/**
	 * Omitted entirely by flows with no video-consent concept (e.g. ambassador drafts), which is
	 * distinct from passing `false` — see the `sapParams` handling below.
	 */
	readonly videoConsent?: boolean;
};

/**
 * Claims a social-starter-style petition angle draft into a full "start a petition" (SAP) draft
 * and navigates to the SAP preview once it's created.
 *
 * Generates an AI petition image from the angle, persists a `user_draft` via the petition-draft
 * API, then navigates to `/start-a-petition/preview` with the params (angle token, user handle,
 * source location, video consent) SAP needs to resume from that draft. Page-agnostic by design —
 * see {@link UseClaimPetitionPageData} — so it can be reused by any flow that shares this
 * "claim an angle draft into SAP" behavior (currently social starter and ambassador drafts).
 */
export function useClaimPetition({
	draft,
	prewarmImageDraft,
	pageData,
	returnPath,
	fallbackErrorMessage = 'claim_petition_failed',
	keepLoadingAfterSuccess = true,
	videoConsent,
}: UseClaimPetitionOptions): UseClaimPetitionReturn {
	const navigate = useNavigate();
	const urlSourceLocation = useQueryParamValue('source_location');
	const utilityContext = useUtilityContext();
	const { username, token: resolvedToken, sourceLocation } = pageData;
	const sapParams = new URLSearchParams();
	if (returnPath) sapParams.set('return_to', returnPath);
	sapParams.set('angle_token', resolvedToken);
	if (username) sapParams.set('user_handle', username);
	appendSourceLocationToSapParams(sapParams, sourceLocation, urlSourceLocation);
	// `video_consent` must be entirely absent from the SAP params when the caller has no
	// video-consent concept, not present with a `false` value.
	if (videoConsent !== undefined) appendVideoConsentToSapParams(sapParams, videoConsent);
	const sapUrl = `/start-a-petition/preview?${sapParams.toString()}`;
	const saveDraft = useSaveClaimedDraft(prewarmImageDraft, resolvedToken, utilityContext);

	const { mutate, error, ...mutationState } = useMutation({
		mutationFn: async () => {
			await saveDraft(draft);
		},
		onSuccess: () => {
			navigate(sapUrl);
		},
		onError: (err) => {
			void utilityContext.errorReporter.report({
				error: err instanceof Error ? err : new Error(fallbackErrorMessage, { cause: err }),
			});
		},
	});

	const startPetition = useCallback(() => {
		mutate();
	}, [mutate]);

	// Stay in the loading state once the mutation succeeds: `onSuccess` triggers a
	// `navigate('/start-a-petition/preview')`, but react-query has already flipped
	// the mutation status back to `success` (loading -> false) by the time React
	// re-renders. Without this, the social starter page flashes back into view for
	// a frame between the loader and the SAP route. Keeping loading=true through
	// `success` lets the loader stay on screen until the page unmounts on navigate.
	const loading = isLoading(mutationState) || (keepLoadingAfterSuccess && isLoaded(mutationState));

	return {
		data: { loading, error },
		actions: { startPetition },
	};
}

type UseClaimPetitionReturn = ModelHookResult<{ loading: boolean; error: Error | null }, { startPetition: () => void }>;
